import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Divider } from "antd";
import { useTranslation } from "react-i18next";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import TagSpecialPackageDetailType from "@components/CommonStyles/TagSpecialPackageDetailType";
import { ECurrency, EOrderType, ESpecialPackageStatus, ETagSpecialPackageDetailType } from "@base/enums";
import useToggle from "@base/hooks/useToggle";
import themeColor from "@base/themes/colorTheme";
import { baseRoutes } from "@base/routes/baseRoutes";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import useGetByRefCode from "@base/modules/order/hooks/useGetByRefCode";
import appointmentService from "@base/modules/appointment/appointmentService";
import { apiLangForRecord } from "@base/modules/appointment/bookingCommon";
import {
  actionKind,
  ActionKind,
  apptKeysToVerify,
  bookableSites,
  bookingWindow,
  canBookPackage,
  canRunContactAction,
  claimedVoucher,
  completedCount,
  confirmationText,
  contactActionHref,
  displayServiceType,
  EffectiveVisitFn,
  extraInfoOf,
  familyMode,
  groupExamCap,
  groupExamMode,
  groupWindowMessage,
  hasCompleteAddress,
  isEVoucher,
  isGroupExamPackage,
  itemsHolder,
  localizedExtra,
  makeEffectiveVisit,
  needsConfirmation,
  nextVisitSequence,
  routePackage,
  serviceProgress,
  serviceSlot,
  ServiceSlot,
  toVnYmd,
  visibleServices,
} from "@base/modules/patientSpecialPackage/packageDetailRules";
import { clearLocalSelection, hasLocalSelection, saveLocalSelection } from "@base/modules/patientSpecialPackage/groupExamSelection";
import {
  ISpecialPackage,
  ISpecialPackageDetail,
  ISpecialPackageDetailImplement,
} from "@base/modules/patientSpecialPackage/patientSpecialPackage.interface";
import patientSpecialPackageService from "@base/modules/patientSpecialPackage/patientSpecialPackageService";
import { ISite } from "@base/modules/site/site.interface";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";
import AddressUpdateModal from "./AddressUpdateModal";
import BeneficiaryPicker from "./BeneficiaryPicker";
import LinkedSitePicker from "./LinkedSitePicker";
import ServicePackageCard from "./ServicePackageCard";
import ServiceItemsPicker, { ServiceItemsMode } from "./ServiceItemsPicker";
import VisitHistoryModal from "./VisitHistoryModal";
import VoucherCode from "./VoucherCode";

/** Appointments found cancelled stay cancelled; looked up once per session (D7). */
const cancelledApptCache = new Map<string, boolean>();

/** A booking under way, filled in step by step before the form opens. */
interface IBookingFlow {
  service: ISpecialPackageDetail;
  visit: ISpecialPackageDetailImplement | null;
  record?: IPatientRecord;
}

/** Phone numbers and links in a confirmation text can be tapped. */
const LinkifiedText = ({ text }: { text: string }) => {
  const parts = text.split(/((?:https?:\/\/|www\.)\S+|(?:\+?\d[\d .]{6,}\d))/g);

  return (
    <span className="whitespace-pre-line">
      {parts.map((part, index) => {
        if (/^(?:https?:\/\/|www\.)/.test(part)) {
          return (
            <a
              key={index}
              href={part.startsWith("www.") ? `https://${part}` : part}
              target="_blank"
              rel="noreferrer"
              className="text-common-primary underline"
            >
              {part}
            </a>
          );
        }

        if (/^\+?\d[\d .]{6,}\d$/.test(part)) {
          return (
            <a key={index} href={`tel:${part.replace(/\D/g, "")}`} className="text-common-primary underline">
              {part}
            </a>
          );
        }

        return part;
      })}
    </span>
  );
};

export default function ServicePackageDetail() {
  //! state
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { key } = useParams();
  const dispatch = useDispatch();
  const { data: resultOrder, fetch: fetchResultOrder } = useGetByRefCode();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [specialPackage, setSpecialPackage] = useState<ISpecialPackage>({});
  const [cancelledKeys, setCancelledKeys] = useState<Set<string>>(new Set());
  const [busyServiceId, setBusyServiceId] = useState<string | number>();

  const { open: openHistory, shouldRender: shouldRenderHistory, toggle: toggleHistory } = useToggle();
  const { open: openItems, shouldRender: shouldRenderItems, toggle: toggleItems } = useToggle();
  const { open: openBeneficiary, shouldRender: shouldRenderBeneficiary, toggle: toggleBeneficiary } = useToggle();
  const { open: openAddressAsk, shouldRender: shouldRenderAddressAsk, toggle: toggleAddressAsk } = useToggle();
  const { open: openAddressForm, shouldRender: shouldRenderAddressForm, toggle: toggleAddressForm } = useToggle();
  const { open: openConfirm, shouldRender: shouldRenderConfirm, toggle: toggleConfirm } = useToggle();
  const { open: openSites, shouldRender: shouldRenderSites, toggle: toggleSites } = useToggle();
  const { open: openVoucher, shouldRender: shouldRenderVoucher, toggle: toggleVoucher } = useToggle();

  const [historyService, setHistoryService] = useState<ISpecialPackageDetail>();
  const [voucherService, setVoucherService] = useState<ISpecialPackageDetail>();
  const [itemsMode, setItemsMode] = useState<ServiceItemsMode>("SELECT");
  const [recordToFix, setRecordToFix] = useState<IPatientRecord>();
  const [lastSiteCode, setLastSiteCode] = useState<string>();
  const flowRef = useRef<IBookingFlow>();
  const afterConfirmRef = useRef<() => void>();

  const route = routePackage(specialPackage);
  const extraInfo = useMemo(() => extraInfoOf(specialPackage), [specialPackage]);
  const packageNotes = localizedExtra(extraInfo, "package_notes", i18n.resolvedLanguage);
  const hotline = typeof extraInfo.site_hotline === "string" ? extraInfo.site_hotline : undefined;
  const eff: EffectiveVisitFn = useMemo(() => makeEffectiveVisit(cancelledKeys), [cancelledKeys]);

  //! function
  const alertError = useCallback(
    (description: string) => dispatch(setAlertMessage({ type: "error", message: t("Notification"), description })),
    [dispatch, t],
  );

  /** An appointment cancelled after booking leaves its use looking booked; ask the appointment itself (D7). */
  const verifyCancelledAppointments = useCallback(async (pkg: ISpecialPackage) => {
    const keys = apptKeysToVerify(pkg);

    await Promise.all(
      keys
        .filter((apptKey) => !cancelledApptCache.has(apptKey))
        .map(async (apptKey) => {
          try {
            const resp = await appointmentService.getByKey(apptKey);

            cancelledApptCache.set(apptKey, String(resp.data?.status ?? "").toUpperCase() === "CANCELLED");
          } catch (error) {
            // Unknown stays as the package says - never unlocked by a failed lookup.
          }
        }),
    );

    setCancelledKeys(new Set(keys.filter((apptKey) => cancelledApptCache.get(apptKey))));
  }, []);

  const getSpecialPackage = useCallback(async () => {
    if (!key) {
      return;
    }

    try {
      const resp = await patientSpecialPackageService.getDetail(key);

      setSpecialPackage(resp.data);
      await verifyCancelledAppointments(resp.data);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }, [key, verifyCancelledAppointments]);

  useEffect(() => {
    getSpecialPackage();
  }, [getSpecialPackage]);

  useEffect(() => {
    if (specialPackage.key) {
      fetchResultOrder({ ref_code: specialPackage.key, order_type: EOrderType.PACKAGE, lang: "vi" });
    }
  }, [specialPackage.key, fetchResultOrder]);

  const runAction = (action: ActionKind) => {
    const href = contactActionHref(action);

    if (!href) {
      return;
    }

    if (action.kind === "GOTO") {
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }

    window.location.href = href;
  };

  const actionLabel = (service: ISpecialPackageDetail) => {
    const action = actionKind(service);

    switch (action.kind) {
      case "CALL":
        return t("packageBooking.actionCall", { value: action.info });
      case "EMAIL":
        return t("booking.actionEmail");
      case "GOTO":
        return t("packageBooking.actionGoto");
      default:
        return "";
    }
  };

  const bookLabel = (service: ISpecialPackageDetail) =>
    actionKind(service).kind === "BOOKING_AND_CONFIRM" ? t("packageBooking.btnBookingConfirm") : t("user.servicePackage.btnBooking");

  const expiredMessage = (visit: ISpecialPackageDetailImplement | null) => {
    if (route !== "GROUP_EXAM") {
      return t("booking.expiredCannotBook");
    }

    const message = groupWindowMessage(specialPackage, bookingWindow(specialPackage, visit), i18n.resolvedLanguage);

    switch (message.kind) {
      case "BACKEND":
        return message.text;
      case "CONDITION_CONFLICT":
        return t("booking.groupExam.windowConflict", message);
      case "RANGE":
        return t("booking.groupExam.windowRange", message);
      case "FROM":
        return t("booking.groupExam.windowFrom", message);
      case "TO":
        return t("booking.groupExam.windowTo", message);
      default:
        return t("booking.groupExam.windowNone");
    }
  };

  /** Asks the BOOKING_AND_CONFIRM question, then carries on. */
  const confirmThen = (next: () => void) => {
    afterConfirmRef.current = next;
    toggleConfirm();
  };

  const goToBooking = (visit: ISpecialPackageDetailImplement, site: ISite, record: IPatientRecord) => {
    const flow = flowRef.current;

    if (!flow) {
      return;
    }

    navigate({
      pathname: baseRoutes.userPackageBooking.replace(":key", String(specialPackage.key)),
      search: createSearchParams({
        detail_code: String(flow.service.code ?? ""),
        detail_id: String(flow.service.id ?? ""),
        implement_id: String(visit.id),
        patient_record_code: String(record.code ?? ""),
        site_code: String(site.code ?? ""),
      }).toString(),
    });
  };

  /** Step 5b: book against the use offered, or open a new one first. */
  const resolveVisit = async (site: ISite) => {
    const flow = flowRef.current;

    if (!flow?.record) {
      return;
    }

    setLastSiteCode(site.code);

    if (flow.visit) {
      goToBooking(flow.visit, site, flow.record);
      return;
    }

    try {
      setBusyServiceId(flow.service.id);
      const resp = await patientSpecialPackageService.createDetailImplement(
        String(flow.service.id),
        nextVisitSequence(flow.service),
        toVnYmd(new Date()),
        apiLangForRecord(flow.record, i18n.resolvedLanguage),
      );

      // A use without an id would book against nothing.
      if (!resp.data?.id) {
        alertError(t("packageBooking.tryAgainLater"));
        return;
      }

      goToBooking(resp.data, site, flow.record);
    } catch (error) {
      const data = (error as { response?: { data?: { message?: string } } })?.response?.data;

      alertError(data?.message || t("booking.genericError"));
    } finally {
      setBusyServiceId(undefined);
    }
  };

  /** Step 5a: the clinic - asked only when there are several. */
  const chooseSite = () => {
    const sites = bookableSites(specialPackage);

    if (!sites.length) {
      alertError(t("packageBooking.noLinkedSite"));
      return;
    }

    if (sites.length === 1) {
      resolveVisit(sites[0]);
      return;
    }

    toggleSites();
  };

  /** Step 4: BOOKING_AND_CONFIRM. */
  const afterAddress = (record: IPatientRecord) => {
    if (!flowRef.current) {
      return;
    }

    flowRef.current.record = record;

    if (needsConfirmation(specialPackage, flowRef.current.service, i18n.resolvedLanguage)) {
      confirmThen(chooseSite);
      return;
    }

    chooseSite();
  };

  /** Step 2: the record must carry a full address. */
  const checkAddress = async (record?: IPatientRecord) => {
    if (!record?.code) {
      alertError(t("packageBooking.tryAgainLater"));
      return;
    }

    try {
      setBusyServiceId(flowRef.current?.service.id);
      const resp = await profileService.getByCode(String(record.code));
      const fullRecord: IPatientRecord = resp.data?.patient_record ?? resp.data ?? record;

      if (!hasCompleteAddress(fullRecord)) {
        setRecordToFix(fullRecord);
        toggleAddressAsk();
        return;
      }

      afterAddress(fullRecord);
    } catch (error) {
      alertError(t("booking.genericError"));
    } finally {
      setBusyServiceId(undefined);
    }
  };

  /** Step 1: who it is for. */
  const askBeneficiary = () => {
    if (familyMode(specialPackage) === "SELF") {
      checkAddress(specialPackage.patient_record);
      return;
    }

    toggleBeneficiary();
  };

  /** A regular package books the use clicked, for its owner, at the site and branch that sold it. */
  const goLegacy = () => {
    const flow = flowRef.current;

    if (!flow?.visit || !specialPackage.patient_record || !specialPackage.site) {
      return;
    }

    flow.record = specialPackage.patient_record;
    goToBooking(flow.visit, specialPackage.site, specialPackage.patient_record);
  };

  /** Every "Book" starts here: expiry first, then the steps of the package's kind (§8.2, §9.2, §10). */
  const startBooking = (service: ISpecialPackageDetail, visit: ISpecialPackageDetailImplement | null) => {
    if (!bookingWindow(specialPackage, visit).bookable) {
      alertError(expiredMessage(visit));
      return;
    }

    flowRef.current = { service, visit };

    if (route === "LEGACY") {
      if (needsConfirmation(specialPackage, service, i18n.resolvedLanguage)) {
        confirmThen(goLegacy);
        return;
      }

      goLegacy();
      return;
    }

    if (route === "GROUP_EXAM" && isGroupExamPackage(specialPackage)) {
      const selectable = groupExamMode(specialPackage) === "SELECTABLE" && !hasLocalSelection(specialPackage.key);

      setItemsMode(selectable ? "SELECT" : "FIXED");
      toggleItems();
      return;
    }

    askBeneficiary();
  };

  const onBookFromHistory = (visit: ISpecialPackageDetailImplement) => {
    const service = historyService;

    toggleHistory();

    if (service) {
      // Wait for the sheet to close so the next dialog does not stack on it.
      setTimeout(() => startBooking(service, visit), 300);
    }
  };

  const claimVoucher = async () => {
    const service = voucherService;

    toggleVoucher();

    if (!service?.id) {
      return;
    }

    try {
      setBusyServiceId(service.id);
      const resp = await patientSpecialPackageService.claimEVoucher(service.id);

      setSpecialPackage((prev) => ({
        ...prev,
        patient_special_package_details: (prev.patient_special_package_details ?? []).map((item) =>
          item.id === service.id ? { ...item, ...resp.data } : item,
        ),
      }));
    } catch (error) {
      alertError(t("booking.genericError"));
    } finally {
      setBusyServiceId(undefined);
    }
  };

  const openHistoryFor = (service: ISpecialPackageDetail) => {
    if (isEVoucher(service) && !claimedVoucher(service)) {
      return;
    }

    setHistoryService(service);
    toggleHistory();
  };

  const handlePayPackage = () => {
    if (!resultOrder?.order_key) {
      alertError(t("booking.cannotPay"));
      return;
    }

    navigate(baseRoutes.userPaymentDetail.replace(":key", resultOrder.order_key));
  };

  //! render
  // Every service keeps its number; whether it is done shows on the card itself.
  const renderDot = (idx: number) => (
    <CommonStyles.TimelineDotUI
      className="p-2 w-8 h-8"
      bgColor={themeColor.blue500}
      borderColor={themeColor.blue500}
      textColor={themeColor.white}
      content={idx + 1}
    />
  );

  const renderTypeTag = (service: ISpecialPackageDetail) => {
    const type = isEVoucher(service) ? undefined : displayServiceType(service.type);

    return type ? <TagSpecialPackageDetailType type={type as ETagSpecialPackageDetailType} /> : null;
  };

  const renderSlot = (service: ISpecialPackageDetail, slot: ServiceSlot) => {
    const isBusy = busyServiceId === service.id;

    switch (slot.kind) {
      case "ACTION":
        return canRunContactAction(specialPackage) ? (
          <CommonStyles.ButtonUI onClick={() => runAction(slot.action)}>{actionLabel(service)}</CommonStyles.ButtonUI>
        ) : null;
      case "EVOUCHER": {
        const voucher = claimedVoucher(service);

        if (voucher) {
          return <VoucherCode code={voucher} size="sm" />;
        }

        return specialPackage.status === ESpecialPackageStatus.IN_PROGRESS && canBookPackage(specialPackage, eff) ? (
          <CommonStyles.ButtonUI
            loading={isBusy}
            onClick={() => {
              setVoucherService(service);
              toggleVoucher();
            }}
          >
            {t("booking.claimVoucher")}
          </CommonStyles.ButtonUI>
        ) : null;
      }
      case "IN_PROGRESS":
      case "BOOKED":
        return (
          <button
            type="button"
            className="flex items-center gap-1 text-common-primary text-sm font-medium font-['Inter']"
            onClick={() => openHistoryFor(service)}
          >
            {slot.kind === "IN_PROGRESS" ? t("booking.visit.IN_PROGRESS") : t("booking.visit.bookingInProgress")}
            <CommonIcons.RightOutlined className="text-xs" />
          </button>
        );
      case "BOOK":
        return (
          <CommonStyles.ButtonUI loading={isBusy} onClick={() => startBooking(service, slot.visit)}>
            {bookLabel(service)}
          </CommonStyles.ButtonUI>
        );
      default:
        return null;
    }
  };

  /** Insurance and group checkup: one card per service, judged by its latest use. */
  const insuranceItems = visibleServices(specialPackage).map((service, idx) => {
    const slot = serviceSlot(specialPackage, service, eff);
    const done = completedCount(service);

    return {
      dot: renderDot(idx),
      children: (
        <div
          onClick={() => openHistoryFor(service)}
          className="bg-common-background rounded-lg p-3 flex justify-between items-center max-sm:flex-col max-sm:items-start gap-4 max-sm:gap-2 cursor-pointer"
        >
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-common-maintext text-sm font-semibold font-['Inter']">{service.item_name}</span>
              {renderTypeTag(service)}
            </div>

            {done > 0 ? (
              <span className="text-common-subtext text-xs font-normal font-['Inter']">{t("packageBooking.usedTimes", { count: done })}</span>
            ) : null}

            {/* A booking service may carry a voucher code of its own; an e-voucher service shows it in its slot. */}
            {!isEVoucher(service) && claimedVoucher(service) ? <VoucherCode code={String(claimedVoucher(service))} /> : null}
          </div>

          <div className="flex gap-2 max-sm:w-full max-sm:justify-end" onClick={(event) => event.stopPropagation()}>
            {renderSlot(service, slot)}
          </div>
        </div>
      ),
    };
  });

  /** Regular package: a row per service with its progress; booking happens in its history. */
  const regularItems = (specialPackage.patient_special_package_details ?? []).map((service, idx) => {
    const progress = serviceProgress(service);
    const price = Number(service.amount ?? 0) * Number(service.qty ?? 0);

    return {
      dot: renderDot(idx),
      children: (
        <div onClick={() => openHistoryFor(service)} className="bg-common-background rounded-lg p-3 flex flex-col gap-1 cursor-pointer">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-common-maintext text-sm font-semibold font-['Inter']">{service.item_name}</span>
              {renderTypeTag(service)}
            </div>

            {price > 0 ? (
              <span className="text-common-inprogress text-sm font-medium font-['Inter']">
                {price.toLocaleString()}
                {ECurrency.VND}
              </span>
            ) : null}
          </div>

          <span className="text-common-subtext text-xs font-normal font-['Inter']">{t(`booking.progress.${progress.kind}`, progress)}</span>
        </div>
      ),
    };
  });

  const items = route === "LEGACY" ? regularItems : insuranceItems;
  const confirmText = flowRef.current ? confirmationText(specialPackage, i18n.resolvedLanguage) : null;
  const holder = itemsHolder(specialPackage);

  if (isLoading) {
    return <CommonStyles.LoadingUI />;
  }

  return (
    <div className="p-5">
      <div className="flex justify-between items-center gap-2">
        <div className="text-common-primary text-base font-bold font-['Inter']">{t("user.servicePackage.servicePackageDetail")}</div>

        {/* Shown for reference only: a desktop browser has nothing sensible to dial with. */}
        {hotline ? (
          <div className="flex items-center gap-2 px-4 py-1 rounded-lg border border-common-primary text-common-primary text-sm font-medium font-['Inter']">
            <CommonIcons.IconPhone />
            <span>{`${t("Hotline")}: ${hotline}`}</span>
          </div>
        ) : null}
      </div>

      <Divider />

      <div className="w-full">
        <ServicePackageCard specialPackage={specialPackage} />

        {packageNotes ? (
          <div className="w-full mt-3 bg-common-blue100 border border-common-blue600 p-3 rounded-lg">
            <CommonStyles.TypographyUI use="paragraph" className="text-common-blue700 text-sm font-normal font-['Inter'] !mb-0 whitespace-pre-line">
              {packageNotes}
            </CommonStyles.TypographyUI>
          </div>
        ) : null}

        {specialPackage.status === ESpecialPackageStatus.CANCELLED && specialPackage.cancelReason ? (
          <div className="w-full mt-3 flex items-center gap-5">
            <CommonStyles.TypographyUI use="paragraph" className="text-[#000000] text-base font-medium font-['Inter'] !mb-0">
              {t("booking.cancelReason")}
            </CommonStyles.TypographyUI>

            <CommonStyles.TypographyUI use="paragraph" className="text-common-negative text-base font-medium font-['Inter'] !mb-0">
              {specialPackage.cancelReason}
            </CommonStyles.TypographyUI>
          </div>
        ) : null}

        <div className="w-full my-3">
          <CommonStyles.TypographyUI use="paragraph" className="text-[#000000] text-base font-medium font-['Inter']">
            {t("booking.packageContents")}
          </CommonStyles.TypographyUI>

          {items.length ? (
            <div className="pt-4 max-h-[610px] overflow-y-auto">
              <CommonStyles.TimelineUI items={items} />
            </div>
          ) : null}

          {specialPackage.status === ESpecialPackageStatus.UNPAID ? (
            <div className="w-full flex justify-center items-center gap-4">
              <CommonStyles.ButtonUI type="primary" onClick={handlePayPackage}>
                {t("payNow")}
              </CommonStyles.ButtonUI>
            </div>
          ) : null}
        </div>
      </div>

      {shouldRenderHistory && historyService && (
        <VisitHistoryModal
          open={openHistory}
          toggle={toggleHistory}
          route={route}
          specialPackage={specialPackage}
          service={historyService}
          eff={eff}
          actionLabel={actionLabel(historyService)}
          bookLabel={bookLabel(historyService)}
          onAction={() => runAction(actionKind(historyService))}
          onBook={onBookFromHistory}
          onDetail={(apptKey) => navigate(baseRoutes.userAppointmentDetail.replace(":key", apptKey))}
        />
      )}

      {shouldRenderItems && holder && (
        <ServiceItemsPicker
          open={openItems}
          toggle={toggleItems}
          mode={itemsMode}
          title={specialPackage.name || specialPackage.package_name}
          packageKey={specialPackage.key}
          items={holder.items ?? []}
          cap={groupExamCap(specialPackage)}
          exceedLimitMessage={localizedExtra(extraInfo, "noti_exceed_value_limit", i18n.resolvedLanguage) ?? undefined}
          onDone={(chosen, outcome, updated) => {
            toggleItems();

            if (outcome === "SAVED" && updated) {
              clearLocalSelection(String(specialPackage.key));
              setSpecialPackage(updated);
            }

            if (outcome === "SYNC_FAILED") {
              saveLocalSelection(String(specialPackage.key), chosen);
              dispatch(setAlertMessage({ type: "warning", message: t("Notification"), description: t("booking.groupExam.notSynced") }));
            }

            askBeneficiary();
          }}
        />
      )}

      {shouldRenderBeneficiary && (
        <BeneficiaryPicker
          open={openBeneficiary}
          toggle={toggleBeneficiary}
          mode={familyMode(specialPackage)}
          ownerRecord={specialPackage.patient_record}
          onPicked={(record) => {
            toggleBeneficiary();
            checkAddress(record);
          }}
        />
      )}

      {shouldRenderAddressAsk && (
        <CommonStyles.ModalConfirmationUI
          open={openAddressAsk}
          toggle={toggleAddressAsk}
          title={t("Notification")}
          content={t("packageBooking.requireAddressUpdate")}
          okText={t("booking.agree")}
          onConfirm={() => {
            toggleAddressAsk();
            toggleAddressForm();
          }}
        />
      )}

      {shouldRenderAddressForm && recordToFix && (
        <AddressUpdateModal
          open={openAddressForm}
          toggle={toggleAddressForm}
          record={recordToFix}
          onSaved={(record) => {
            toggleAddressForm();
            afterAddress(record);
          }}
        />
      )}

      {shouldRenderConfirm && (
        <CommonStyles.ModalConfirmationUI
          open={openConfirm}
          toggle={toggleConfirm}
          title={t("booking.confirmBookingTitle")}
          okText={t("booking.agree")}
          content={confirmText ? <LinkifiedText text={confirmText} /> : null}
          onConfirm={() => {
            toggleConfirm();
            afterConfirmRef.current?.();
          }}
        />
      )}

      {shouldRenderSites && (
        <LinkedSitePicker
          open={openSites}
          toggle={toggleSites}
          sites={bookableSites(specialPackage)}
          selectedCode={lastSiteCode}
          onPicked={(site) => {
            toggleSites();
            resolveVisit(site);
          }}
        />
      )}

      {shouldRenderVoucher && (
        <CommonStyles.ModalConfirmationUI
          open={openVoucher}
          toggle={toggleVoucher}
          title={t("Notification")}
          content={t("booking.claimVoucherConfirm")}
          okText={t("booking.agree")}
          onConfirm={claimVoucher}
        />
      )}
    </div>
  );
}
